/**
 * One operation in the activity history, and what it did when opened.
 *
 * An operation is one API or MCP call, however many rows it changed -- so the
 * list is short even when the history is enormous, and a line is only expanded
 * into rows when somebody asks for it. That is also why the fields are fetched
 * here rather than up front: rendering a change needs the schema of whichever
 * tables it touched, and a database of forty tables would otherwise cost forty
 * requests to open a dialog nobody has expanded anything in.
 */
import { useState } from 'react'
import { useQueries } from '@tanstack/react-query'
import { ChevronRight, Undo2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { DatabaseApplication, DatabaseRowHistoryGroup } from '@/api/types'
import { useSessionStore } from '@/features/auth/session-store'
import { fieldsQuery } from '@/features/database/table/queries'
import { useFieldContext } from '@/features/database/fields/ui/context'
import { historyChanges, historyPhrase } from '@/features/database/row/history'
import { HistoryChanges } from '@/features/database/row/history-changes'
import { whenText } from '@/shared/lib/when'
import { Badge } from '@/shared/ui/badge'
import { Button } from '@/shared/ui/button'
import { Skeleton } from '@/shared/ui/skeleton'
import { sourceOf, type ActivityEntry } from '../activity'
import { useOperationRows, type RevertTarget } from '../queries'

export function OperationRow({
  databaseId,
  workspaceId,
  operation,
  tables,
  onRevert,
}: {
  databaseId: number
  workspaceId: number
  operation: DatabaseRowHistoryGroup
  tables: DatabaseApplication['tables']
  onRevert: (target: RevertTarget) => void
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const userId = useSessionStore((state) => state.user?.id)

  const source = sourceOf(operation.source)
  const phrase = historyPhrase(operation.action_type)
  const who = operation.user.id === userId ? t('history.you') : operation.user.name

  const named = operation.table_ids
    .map((id) => tables.find((table) => table.id === id)?.name)
    .filter((name): name is string => name !== undefined)

  return (
    <li className="rounded border">
      <div className="flex items-start gap-2 p-2">
        <button
          type="button"
          aria-expanded={open}
          className="flex min-w-0 flex-1 items-start gap-2 text-left"
          onClick={() => setOpen(!open)}
        >
          <ChevronRight
            className={`mt-0.5 size-4 shrink-0 text-muted-foreground transition-transform ${
              open ? 'rotate-90' : ''
            }`}
            aria-hidden
          />
          <span className="min-w-0">
            <span className="text-sm">
              <span className="font-medium">{who}</span>{' '}
              {phrase === null
                ? t('history.did', { action: operation.action_type })
                : t(`activity.${phrase}`, { count: operation.row_count })}
              {operation.action_command_type === 'UNDO' && <> · {t('history.undone')}</>}
              {operation.action_command_type === 'REDO' && <> · {t('history.redone')}</>}
            </span>
            <span className="mt-0.5 block truncate text-xs text-muted-foreground">
              {/*
                Named rather than counted. "3 tables" is a number somebody then
                has to go and expand the operation to make sense of.
              */}
              {named.length > 0 && <>{named.join(', ')} · </>}
              {whenText(operation.timestamp)}
            </span>
          </span>
        </button>

        {/*
          A source this build does not recognise is named as it stands rather
          than hidden or called something else -- where a write came in through
          is the fact this whole list exists to report.
        */}
        <Badge variant={source === 'mcp' ? 'default' : 'secondary'} className="shrink-0">
          {source === null ? operation.source : t(`activity.sources.${source}`)}
        </Badge>
      </div>

      {open && (
        <OperationDetail
          databaseId={databaseId}
          workspaceId={workspaceId}
          actionUuid={operation.action_uuid}
          tables={tables}
          onRevert={onRevert}
        />
      )}
    </li>
  )
}

function OperationDetail({
  databaseId,
  workspaceId,
  actionUuid,
  tables,
  onRevert,
}: {
  databaseId: number
  workspaceId: number
  actionUuid: string
  tables: DatabaseApplication['tables']
  onRevert: (target: RevertTarget) => void
}) {
  const { t } = useTranslation()
  const context = useFieldContext(workspaceId)
  const rows = useOperationRows(databaseId, actionUuid, true)

  // The tables the *entries* name, not the ones the summary does. A change to a
  // link row leaves a mirror entry on the table at the other end, and the
  // summary is not obliged to have counted that table among its own.
  const entries = (rows.data?.results ?? []) as unknown as ActivityEntry[]
  const tableIds = [...new Set(entries.map((entry) => entry.table_id))]

  const schemas = useQueries({
    queries: tableIds.map((tableId) => fieldsQuery(tableId)),
  })
  const fieldsFor = new Map(
    tableIds.map((tableId, index) => [tableId, schemas[index]?.data ?? []]),
  )

  // Every entry of one operation shares its batch, so the first that has one
  // answers for all of them. It is absent only on a record written before the
  // column existed.
  const actionGroup = entries.find((entry) => entry.action_group != null)?.action_group

  return (
    <div className="border-t px-2 py-2 pl-8">
      {rows.isPending ? (
        <Skeleton className="h-12 w-full" />
      ) : entries.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('activity.noRows')}</p>
      ) : (
        <>
          <ul className="space-y-3">
            {entries.map((entry) => (
              <li key={entry.id}>
                <p className="text-xs text-muted-foreground">
                  {tables.find((table) => table.id === entry.table_id)?.name ??
                    t('activity.unknownTable')}{' '}
                  · {t('activity.row', { id: entry.row_id })}
                </p>
                <HistoryChanges
                  changes={historyChanges(
                    entry,
                    fieldsFor.get(entry.table_id) ?? [],
                    context,
                  )}
                />
              </li>
            ))}
          </ul>
          {rows.data !== undefined && rows.data.count > entries.length && (
            <p className="mt-2 text-xs text-muted-foreground">
              {t('activity.andMoreRows', { count: rows.data.count - entries.length })}
            </p>
          )}
        </>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onRevert({ kind: 'operation', actionUuid })}
        >
          <Undo2 />
          {t('activity.revertOperation')}
        </Button>
        {/*
          Offered only once an entry has said which batch this was part of. For
          an agent a batch is one session, so this is the control that undoes an
          afternoon's work in one go -- which is why it says so plainly and why
          it is not the first button.
        */}
        {actionGroup != null && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onRevert({ kind: 'batch', actionGroup })}
          >
            <Undo2 />
            {t('activity.revertBatch')}
          </Button>
        )}
      </div>
    </div>
  )
}
